import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next'
import { Stack, Button, Box } from '@mui/material';

import {Header} from 'components/Modal';

import StepOtrumWarning from './StepOtrumWarning'
import ModelSteps, { Slider } from './components';
import { useRoom } from '../../use-rooms';
import { useSync } from './hooks'
import { trackEvent } from 'analytics'

const OPERATION_ZIP = gql`
  query tvSettingsZip($sf_id: String!, $ssid_name: String!, $wifi_psk: String!, $name: String!) {
    base64: tvSettingsZip(sf_id: $sf_id, ssid_name: $ssid_name, wifi_psk: $wifi_psk, name: $name)
  }
`

const Philips = ({ deviceType, device, psk, pin }) => {
  const [step, setStep] = useState(0);
  const { t } = useTranslation();
  const room = useRoom()
  const [syncTvsMacAddress, { loading: loadingSync }] = useSync()
  const [tvSettingsZip, { loading: loadingZip }] = useLazyQuery(OPERATION_ZIP);
  
  const hasChromecast = useMemo(() => room && room.devices.find(({ kind }) => kind === 'chromecast'), [room, device.uuid]);
  const navigate = useNavigate();

  const titles = useMemo<string[]>(() => {
    return [
      t('stream-cast.config.philips.block_1_title'),
      t('stream-cast.config.philips.block_2_title'),
      t('stream-cast.config.otrum_warning_title'),
      t('stream-cast.config.otrum_title'),
      t('stream-cast.config.philips.block_4_title')
    ].filter(Boolean) as string[]
  }, [])
  const isWarningStep = useMemo<boolean>(() => {
    return step === titles.length - 3
  }, [step, titles])
  const handleDownload = async () => {
    const { data } = await tvSettingsZip({
      variables: {
        sf_id: room?.site_sf_id,
        ssid_name: room?.ssid_name,
        wifi_psk: psk,
        name: device.sub_room ? `${device.room} | ${device.sub_room}` : device.room
      }
    });
    const response = await fetch(data.base64)
    const blob = await response.blob()
    const fileUrl = URL.createObjectURL(blob)
    trackEvent('stream_cast_settings_downloaded', { manufacturer: 'philips' })
    window.open(fileUrl, '_blank')
  }
  return (
    <Stack sx={{ width: 500, fontSize: 13 }}>
      <ModelSteps
        loading={loadingSync || loadingZip}
        disabledNext={isWarningStep}
        nbSteps={device.mac ? 4 : 5}
        step={step}
        onSelect={async (value) => {
          if (value === 4 && !device.mac) {
            await syncTvsMacAddress();
          } else if ([4, 5].includes(value)) {
            trackEvent('stream_cast_config_completed', { manufacturer: 'philips', device_type: deviceType })
            navigate('..')
          }
          setStep(value)

        }}>
        <Header icon={<i className="fa-solid fa-gear" />} title={titles[step] || ''} />
        {step === 0 && (
          <>
            <ul className="stream-cast_list">
              <li><Button variant="contained" color="primary" onClick={handleDownload} size="small">{t('stream-cast.config.philips.download_button')}</Button></li>
            </ul>
            <Box sx={{ fontSize: 13 }} dangerouslySetInnerHTML={{ __html: t('stream-cast.config.philips.block_1') }} />
          </>
        )}
        {step === 1 && deviceType === 'old' && <Box dangerouslySetInnerHTML={{ __html: t('stream-cast.config.philips.block_2_old') }} />}
        {step === 1 && deviceType === 'new' && <Box dangerouslySetInnerHTML={{ __html: t('stream-cast.config.philips.block_2_new') }} />}
        {isWarningStep && <StepOtrumWarning onValidate={() => setStep(prev => prev + 1)} />}
        {(step === titles.length - 2) && <Box dangerouslySetInnerHTML={{ __html: t('stream-cast.config.otrum', {pin, room: device.subroom ? `${device.room} | ${device.subroom}` : device.room}) }} />}
        {(step === titles.length - 2) && hasChromecast && <Box dangerouslySetInnerHTML={{ __html: t('stream-cast.config.otrum_chromecast_disclaimer') }} />}
        {(step === titles.length - 1) && <Box dangerouslySetInnerHTML={{ __html: t('stream-cast.config.philips.block_4') }} />}
      </ModelSteps>
      <Slider nbSteps={titles.length} step={step} onSelect={setStep} />
    </Stack>
  )
}

export default Philips;